import { lazy, Suspense, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { Loader2 } from "lucide-react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PostHogPageTracker } from "@/components/PostHogPageTracker";

// Public pages (eagerly loaded — marketing/conversion critical)
import NewHome from "./pages/NewHome";
import Demo from "./pages/Demo";
import Pricing from "./pages/Pricing";
import Login from "./pages/Login";
import AuthCallback from "./pages/auth/Callback";
import NotFound from "./pages/NotFound";

// Lazy-loaded public pages (less critical)
const Index = lazy(() => import("./pages/Index"));
const Signup = lazy(() => import("./pages/Signup"));
const SignupSuccess = lazy(() => import("./pages/SignupSuccess"));
const RequestDemo = lazy(() => import("./pages/RequestDemo"));
const About = lazy(() => import("./pages/About"));
const Receptionist = lazy(() => import("./pages/products/Receptionist"));
const Website = lazy(() => import("./pages/products/Website"));
const Reputation = lazy(() => import("./pages/products/Reputation"));
const LocalSeo = lazy(() => import("./pages/products/LocalSeo"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const SubscriptionAgreement = lazy(() => import("./pages/SubscriptionAgreement"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Solutions = lazy(() => import("./pages/Solutions"));
const MortgageBrokers = lazy(() => import("./pages/solutions/MortgageBrokers"));
const ForBrokers = lazy(() => import("./pages/ForBrokers"));

// Sales Portal (lazy — only sales reps)
import { SalesLayout } from "./components/layout/SalesLayout";
const SalesDashboard = lazy(() => import("./pages/sales/SalesDashboard"));
const ActiveDemos = lazy(() => import("./pages/sales/ActiveDemos"));
const CreateDemo = lazy(() => import("./pages/sales/CreateDemo"));
const DemoDetail = lazy(() => import("./pages/sales/DemoDetail"));
const Pipeline = lazy(() => import("./pages/sales/Pipeline"));
const Prospects = lazy(() => import("./pages/sales/Prospects"));
const Pool = lazy(() => import("./pages/sales/Pool"));
const History = lazy(() => import("./pages/sales/History"));
const Onboarding = lazy(() => import("./pages/sales/Onboarding"));
const OnboardingQueue = lazy(() => import("./pages/sales/OnboardingQueue"));
const OnboardingSetup = lazy(() => import("./pages/sales/OnboardingSetup"));
const SalesAnalytics = lazy(() => import("./pages/sales/Analytics"));
const Websites = lazy(() => import("./pages/sales/Websites"));
const WebsiteDetail = lazy(() => import("./pages/sales/WebsiteDetail"));
const DeployWebsite = lazy(() => import("./pages/sales/DeployWebsite"));
const WebsiteStudio = lazy(() => import("./pages/sales/WebsiteStudio"));
const MortgageStudio = lazy(() => import("./pages/sales/MortgageStudio"));
const BrokerManagement = lazy(() => import("./pages/sales/BrokerManagement"));
const BrokerDetail = lazy(() => import("./pages/sales/BrokerDetail"));
const SalesDeck = lazy(() => import("./pages/sales/SalesDeck"));
const BrokerProspects = lazy(() => import("./pages/sales/BrokerProspects"));
const ContentDrafts = lazy(() => import("./pages/sales/social/ContentDrafts"));
const DraftReview = lazy(() => import("./pages/sales/social/DraftReview"));
const ContentCalendar = lazy(() => import("./pages/sales/social/ContentCalendar"));

// Customer Onboarding (lazy — one-time use)
const Survey = lazy(() => import("./pages/onboarding/Survey"));
const SurveyComplete = lazy(() => import("./pages/onboarding/SurveyComplete"));

// Business Portal (lazy — only business owners)
import { TenantLayout } from "./components/layout/TenantLayout";
const BusinessDashboard = lazy(() => import("./pages/app/dashboard/index"));
const CallsPage = lazy(() => import("./pages/app/dashboard/calls"));
const JobsPage = lazy(() => import("./pages/app/dashboard/jobs"));
const JobDetailPage = lazy(() => import("./pages/app/dashboard/jobs/[id]"));
const DispatchDashboard = lazy(() => import("./pages/app/dashboard/dispatch"));
const ServicesPage = lazy(() => import("./pages/app/dashboard/services"));
const FAQsPage = lazy(() => import("./pages/app/dashboard/faqs"));
const CustomersPage = lazy(() => import("./pages/app/dashboard/customers"));
const CustomerDetailPage = lazy(() => import("./pages/app/dashboard/customers/[id]"));
const TechniciansPage = lazy(() => import("./pages/app/dashboard/technicians"));
const SchedulePage = lazy(() => import("./pages/app/dashboard/schedule"));
const SettingsPage = lazy(() => import("./pages/app/dashboard/settings"));
const AnalyticsPage = lazy(() => import("./pages/app/dashboard/analytics"));
const HelpPage = lazy(() => import("./pages/app/dashboard/help"));
const EmailPreview = lazy(() => import("./pages/app/dashboard/EmailPreview"));

// Technician App (lazy — only technicians)
import { TechLayout } from "./components/layout/TechLayout";
const TechHome = lazy(() => import("./pages/app/tech/index"));
const TechJobsList = lazy(() => import("./pages/app/tech/jobs"));
const TechJobDetail = lazy(() => import("./pages/app/tech/jobs/[id]"));
const TechSurvey = lazy(() => import("./pages/app/tech/survey"));
const TechProfile = lazy(() => import("./pages/app/tech/profile"));

// Admin Pages (lazy — rarely used)
const SeedDemoPage = lazy(() => import("./pages/admin/seed-demo"));

// Suspense fallback for lazy-loaded routes
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="h-8 w-8 animate-spin text-lime-500" />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 30_000,    // 30s before data considered stale
      gcTime: 5 * 60_000,   // 5min garbage collection
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: 1,              // One retry for writes (server may have already applied)
    },
  },
});

/** Announces route changes to screen readers via aria-live region */
function RouteAnnouncer() {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    // Small delay so the new page title has time to render
    const timer = setTimeout(() => {
      setAnnouncement(document.title || "Page loaded");
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <div
      role="status"
      aria-live="assertive"
      aria-atomic="true"
      className="sr-only"
    >
      {announcement}
    </div>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <PostHogPageTracker />
          <RouteAnnouncer />
          <ThemeProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<NewHome />} />
                  <Route path="/old-home" element={<Index />} />
                  <Route path="/demo" element={<Demo />} />
                  <Route path="/how-it-works" element={<HowItWorks />} />
                  <Route path="/solutions" element={<Solutions />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/signup" element={<Navigate to="/pricing" replace />} />
                  <Route path="/signup/success" element={<SignupSuccess />} />
                  <Route path="/request-demo" element={<RequestDemo />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/features" element={<Navigate to="/solutions" replace />} />
                  <Route path="/products/receptionist" element={<Receptionist />} />
                  <Route path="/products/website" element={<Website />} />
                  <Route path="/products/reputation" element={<Reputation />} />
                  <Route path="/products/local-seo" element={<LocalSeo />} />
                  <Route path="/solutions/mortgage-brokers" element={<MortgageBrokers />} />
                  <Route path="/for-brokers" element={<ForBrokers />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/subscription-agreement" element={<SubscriptionAgreement />} />

                  {/* Customer Onboarding - Public (token-protected) */}
                  <Route path="/onboarding/survey" element={<Survey />} />
                  <Route path="/onboarding/survey-complete" element={<SurveyComplete />} />

                  {/* Sales Deck - Full-screen presentation (no layout chrome) */}
                  <Route
                    path="/sales/deck"
                    element={
                      <ProtectedRoute requiredPortal="sales">
                        <SalesDeck />
                      </ProtectedRoute>
                    }
                  />

                  {/* Sales Portal - Protected */}
                  <Route
                    path="/sales"
                    element={
                      <ProtectedRoute requiredPortal="sales">
                        <SalesLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<SalesDashboard />} />
                    <Route path="demos" element={<ActiveDemos />} />
                    <Route path="demos/new" element={<CreateDemo />} />
                    <Route path="demos/:id" element={<DemoDetail />} />
                    <Route path="pipeline" element={<Pipeline />} />
                    <Route path="prospects" element={<Prospects />} />
                    <Route path="pool" element={<Pool />} />
                    <Route path="analytics" element={<SalesAnalytics />} />
                    <Route path="history" element={<History />} />
                    <Route path="onboarding" element={<Onboarding />} />
                    <Route path="onboarding/queue" element={<OnboardingQueue />} />
                    <Route path="onboarding/:tenantId" element={<OnboardingSetup />} />
                    <Route path="websites" element={<Websites />} />
                    <Route path="websites/deploy" element={<DeployWebsite />} />
                    <Route path="websites/studio" element={<WebsiteStudio />} />
                    <Route path="websites/mortgage-studio" element={<MortgageStudio />} />
                    <Route path="websites/:id" element={<WebsiteDetail />} />
                    <Route path="brokers" element={<BrokerManagement />} />
                    <Route path="broker-prospects" element={<BrokerProspects />} />
                    <Route path="social/drafts" element={<ContentDrafts />} />
                    <Route path="social/drafts/:draftId" element={<DraftReview />} />
                    <Route path="social/calendar" element={<ContentCalendar />} />
                    <Route path="brokers/:tenantId" element={<BrokerDetail />} />
                  </Route>

                  {/* Business Portal - Protected */}
                  <Route
                    path="/app/dashboard"
                    element={
                      <ProtectedRoute requiredPortal="business">
                        <TenantLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<BusinessDashboard />} />
                    <Route path="calls" element={<CallsPage />} />
                    <Route path="jobs" element={<JobsPage />} />
                    <Route path="jobs/:id" element={<JobDetailPage />} />
                    <Route
                      path="dispatch"
                      element={
                        <ProtectedRoute requiredFeature="smartDispatch">
                          <DispatchDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="services" element={<ServicesPage />} />
                    <Route path="faqs" element={<FAQsPage />} />
                    <Route path="customers" element={<CustomersPage />} />
                    <Route path="customers/:id" element={<CustomerDetailPage />} />
                    <Route
                      path="technicians"
                      element={
                        <ProtectedRoute requiredFeature="techPortal">
                          <TechniciansPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="schedule" element={<SchedulePage />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="help" element={<HelpPage />} />
                    <Route path="email-preview" element={<EmailPreview />} />
                    <Route path="social-studio" element={<ContentDrafts />} />
                    <Route path="social-studio/:draftId" element={<DraftReview />} />
                    <Route path="social-studio/calendar" element={<ContentCalendar />} />
                  </Route>

                  {/* Technician App - Protected (Professional tier and above) */}
                  <Route
                    path="/app/tech"
                    element={
                      <ProtectedRoute requiredPortal="technician" requiredFeature="techPortal">
                        <TechLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<TechHome />} />
                    <Route path="jobs" element={<TechJobsList />} />
                    <Route path="jobs/:id" element={<TechJobDetail />} />
                    <Route path="survey" element={<TechSurvey />} />
                    <Route path="profile" element={<TechProfile />} />
                  </Route>

                  {/* Social Studio — top-level alias of the /app/dashboard/social-studio
                      routes above. These previously rendered with NO auth wrapper, exposing
                      broker content drafts to anyone who guessed the URL. Gated to match
                      their protected siblings (see :273-275). */}
                  <Route
                    path="/social-studio"
                    element={
                      <ProtectedRoute requiredPortal="business">
                        <ContentDrafts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/social-studio/:draftId"
                    element={
                      <ProtectedRoute requiredPortal="business">
                        <DraftReview />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/social-studio/calendar"
                    element={
                      <ProtectedRoute requiredPortal="business">
                        <ContentCalendar />
                      </ProtectedRoute>
                    }
                  />

                  {/* Admin Routes - Master accounts only */}
                  <Route path="/admin/seed-demo" element={<SeedDemoPage />} />

                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </TooltipProvider>
          </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </MotionConfig>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
